import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import { LoginSelectors } from 'modules/landingPage/components/login/redux';
import { useToastContainer } from 'modules/layout/toastContainer';
import { ProfileSelectors, ProfileThunks } from 'modules/profilePage/redux';
import { InputWithLabel } from 'modules/ui';
import { FormButtons, FormContainer } from 'modules/ui/form';
import { ToastType } from 'modules/ui/toast';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateUserData } from 'modules/types/User';
import { ValidationType } from '../../../../types/ValidationType';
import { PHONE_NUMBER_PREFIX } from '../../../hooks/validationHooks';
import { useForm } from '../../../ui/form/useForm';
import { MaxFieldLength } from '../../../ui/input/common/MaxFieldLength';
import { getDefaultUserUpdateData } from '../helper';

export const EditContactPersonForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { addToast } = useToastContainer();

  const [validate, setValidate] = useState<boolean>(false);
  const { validateForm } = useForm();

  const user = useAppSelector(LoginSelectors.selectUser);
  const updateContactSuccess = useAppSelector(
    ProfileSelectors.selectUpdateProfileDataSuccess,
  );
  const updateContactError = useAppSelector(
    ProfileSelectors.selectUpdateProfileDataError,
  );

  const [formData, setFormData] = useState<UpdateUserData>(
    getDefaultUserUpdateData(user),
  );
  const [buttonClicked, setButtonClicked] = useState(false);

  const handleClick = (event: any) => {
    event.preventDefault();
    setValidate(true);
    if (!validateForm(event)) {
      setButtonClicked(true);
      dispatch(ProfileThunks.updateProfileData(formData));
    }
  };

  useEffect(() => {
    if (updateContactSuccess && buttonClicked) {
      addToast({
        title: t('profilePage.contactPersonModification.info'),
        text: t('profilePage.contactPersonModification.successMessage'),
      });
      setButtonClicked(false);
    } else if (updateContactError && buttonClicked) {
      addToast({
        title: t('profilePage.contactPersonModification.info'),
        text: t('profilePage.contactPersonModification.errorMessage'),
        type: ToastType.Urgent,
      });
      setButtonClicked(false);
    }
  }, [addToast, buttonClicked, t, updateContactError, updateContactSuccess]);

  return (
    <FormContainer noValidate onSubmit={handleClick}>
      <InputWithLabel
        required
        label={t('profilePage.contactPersonModification.contactPerson')}
        placeholderText={t(
          'profilePage.contactPersonModification.contactPersonPlaceholder',
        )}
        defaultValue={formData.contactPersonName}
        onBlur={(e) =>
          setFormData({ ...formData, contactPersonName: e.target.value })
        }
        maxLength={MaxFieldLength.ContactPerson}
        validators={[ValidationType.REQUIRED]}
        validate={validate}
      />
      <InputWithLabel
        required
        label={t('profilePage.contactPersonModification.contactPhone')}
        placeholderText={t(
          'profilePage.contactPersonModification.contactPhonePlaceholder',
        )}
        defaultValue={formData.contactPersonPhone?.number}
        onBlur={(e) =>
          setFormData({
            ...formData,
            contactPersonPhone: {
              areaCode:
                formData.contactPersonPhone?.areaCode || PHONE_NUMBER_PREFIX,
              number: e.target.value,
            },
          })
        }
        maxLength={MaxFieldLength.Phone}
        prefix={PHONE_NUMBER_PREFIX}
        validators={[ValidationType.REQUIRED, ValidationType.PHONE]}
        validate={validate}
      />

      <FormButtons text={t('profilePage.save')} />
    </FormContainer>
  );
};
